/* True-size sticker preview for the printable sticker size guide.
   The shopper calibrates the screen against a real coin or bank card, picks a
   shape and size, and the outline is drawn at true physical size next to
   familiar objects for scale. */
(function () {
  var REFERENCES = {
    coin: { inches: 0.945, label: 'Quarter', note: 'Put a real quarter on the circle, then slide until they match.' },
    card: { inches: 3.37, label: 'Bank card', note: 'Hold a bank or loyalty card on the outline, then slide until the long edges match.' },
  };

  var CARD_SHORT_INCHES = 2.125;
  var STORAGE_KEY = 'spark-size-guide-ppi';
  var MIN_PPI = 60;
  var MAX_PPI = 320;
  var MIN_CUSTOM = 0.5;
  var MAX_CUSTOM = 12;

  var PRESETS = {
    circle: [[1, 1], [2, 2], [3, 3], [4, 4]],
    square: [[1, 1], [2, 2], [3, 3], [4, 4]],
    rectangle: [[2, 1], [3, 2], [4, 3], [5, 3]],
    oval: [[2, 1], [3, 2], [4, 3], [5, 3]],
  };

  var ROUND = { circle: true, oval: true };
  var LOCKED = { circle: true, square: true };

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function mm(inches) {
    return Math.round(inches * 25.4 * 10) / 10;
  }

  function sizeLabel(shape, width, height) {
    if (shape === 'circle') return trim(width) + ' in across';
    return trim(width) + ' × ' + trim(height) + ' in';
  }

  function trim(value) {
    return String(Math.round(value * 100) / 100);
  }

  function publishSelection(root, currentShape, currentSize) {
    var width = currentSize.width;
    var height = LOCKED[currentShape] ? width : currentSize.height;
    root.dispatchEvent(new CustomEvent('spark:size-guide-selection', {
      bubbles: true,
      detail: {
        shape: currentShape,
        widthIn: width,
        heightIn: height,
      },
    }));
  }

  function readStoredPpi() {
    try {
      var stored = parseFloat(window.localStorage.getItem(STORAGE_KEY));
      return isFinite(stored) ? clamp(stored, MIN_PPI, MAX_PPI) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredPpi(ppi) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Math.round(ppi * 100) / 100));
    } catch (error) {
      /* Storage is optional. */
    }
  }

  function measureCssInch(root) {
    var probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;width:1in;height:0;visibility:hidden;pointer-events:none;';
    root.appendChild(probe);
    var width = probe.getBoundingClientRect().width;
    root.removeChild(probe);
    return width > 0 ? width : 96;
  }

  function init(root) {
    var slider = root.querySelector('[data-truesize-slider]');
    var target = root.querySelector('[data-truesize-target]');
    var targetLabel = root.querySelector('[data-truesize-target-label]');
    var note = root.querySelector('[data-truesize-note]');
    var stage = root.querySelector('[data-truesize-stage]');
    var shapeEl = root.querySelector('[data-truesize-shape]');
    var shapeText = root.querySelector('[data-truesize-shape-label]');
    var status = root.querySelector('[data-truesize-status]');
    var reset = root.querySelector('[data-truesize-reset]');
    var custom = root.querySelector('[data-truesize-custom]');
    var widthInput = root.querySelector('[data-truesize-custom-width]');
    var heightInput = root.querySelector('[data-truesize-custom-height]');
    var heightField = root.querySelector('[data-truesize-height-field]');
    var customEcho = root.querySelector('[data-truesize-custom-mm]');
    var sizeRow = root.querySelector('[data-truesize-sizes]');
    var refToggle = root.querySelector('[data-truesize-compare]');
    var refs = root.querySelector('[data-truesize-refs]');
    var coin = root.querySelector('[data-truesize-coin]');
    var cardRef = root.querySelector('[data-truesize-cardref]');
    var shapeButtons = [].slice.call(root.querySelectorAll('[data-truesize-shape-option]'));
    var refButtons = [].slice.call(root.querySelectorAll('[data-truesize-reference]'));

    if (!slider || !target || !stage || !shapeEl || !sizeRow || !shapeButtons.length) return;

    var defaultPpi = measureCssInch(root);
    var ppi = readStoredPpi() || defaultPpi;
    var shape = 'circle';
    // A coin is the default because it fits every screen, including a phone.
    // A bank card is offered too, but its 3.37 in long edge is wider than most phones.
    var reference = 'coin';
    var size = { width: 2, height: 2 };
    var isCustom = false;

    function activeShapeButton() {
      for (var i = 0; i < shapeButtons.length; i += 1) {
        if (shapeButtons[i].getAttribute('aria-pressed') === 'true') return shapeButtons[i];
      }
      return shapeButtons[0];
    }

    function buildSizeRow() {
      var presets = PRESETS[shape];
      sizeRow.innerHTML = '';

      presets.forEach(function (preset) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'spark-size-guide__truesize-chip';
        button.textContent = shape === 'circle' ? trim(preset[0]) + '″' : trim(preset[0]) + ' × ' + trim(preset[1]);
        var pressed = !isCustom && preset[0] === size.width && preset[1] === size.height;
        button.setAttribute('aria-pressed', String(pressed));
        button.addEventListener('click', function () {
          isCustom = false;
          size = { width: preset[0], height: preset[1] };
          syncCustomInputs();
          buildSizeRow();
          render();
        });
        sizeRow.appendChild(button);
      });

      var customButton = document.createElement('button');
      customButton.type = 'button';
      customButton.className = 'spark-size-guide__truesize-chip spark-size-guide__truesize-chip--custom';
      customButton.textContent = 'Custom size';
      customButton.setAttribute('aria-pressed', String(isCustom));
      customButton.setAttribute('aria-expanded', String(isCustom));
      customButton.addEventListener('click', function () {
        isCustom = true;
        syncCustomInputs();
        buildSizeRow();
        render();
        if (widthInput) widthInput.focus();
      });
      sizeRow.appendChild(customButton);
    }

    function syncCustomInputs() {
      if (widthInput) widthInput.value = trim(size.width);
      if (heightInput) heightInput.value = trim(size.height);
    }

    function readCustomInputs() {
      var width = clamp(parseFloat(widthInput && widthInput.value) || MIN_CUSTOM, MIN_CUSTOM, MAX_CUSTOM);
      var height = LOCKED[shape]
        ? width
        : clamp(parseFloat(heightInput && heightInput.value) || MIN_CUSTOM, MIN_CUSTOM, MAX_CUSTOM);
      size = { width: width, height: height };
    }

    function applyShape() {
      var locked = !!LOCKED[shape];
      if (heightField) heightField.hidden = locked;
      if (locked) size.height = size.width;

      var presets = PRESETS[shape];
      if (!isCustom) {
        var match = presets.filter(function (preset) {
          return preset[0] === size.width && preset[1] === size.height;
        })[0];
        if (!match) size = { width: presets[1][0], height: presets[1][1] };
      }
      syncCustomInputs();
      buildSizeRow();
    }

    function renderTarget() {
      var config = REFERENCES[reference];
      var room = target.parentNode ? target.parentNode.clientWidth : 0;
      var wide = config.inches * ppi;
      var landscape = reference !== 'card' || room === 0 || wide <= room;
      var edge = landscape ? config.inches : CARD_SHORT_INCHES;

      target.style.width = edge * ppi + 'px';
      target.style.height = (reference === 'coin' ? edge : (landscape ? CARD_SHORT_INCHES : config.inches)) * ppi + 'px';
      target.classList.toggle('is-round', reference === 'coin');

      if (targetLabel) targetLabel.textContent = config.label;
      if (note) {
        note.textContent = landscape
          ? config.note
          : 'Hold a bank or loyalty card across the outline and slide until the short edges match.';
      }
    }

    function renderReferences() {
      if (!refs) return;
      var on = !refToggle || refToggle.getAttribute('aria-pressed') === 'true';
      refs.hidden = !on;
      if (!on) return;
      if (coin) {
        coin.style.width = REFERENCES.coin.inches * ppi + 'px';
        coin.style.height = REFERENCES.coin.inches * ppi + 'px';
      }
      if (cardRef) {
        cardRef.style.width = REFERENCES.card.inches * ppi + 'px';
        cardRef.style.height = CARD_SHORT_INCHES * ppi + 'px';
      }
    }

    function render() {
      var available = stage.clientWidth - 24;
      var wanted = size.width * ppi;
      var factor = available > 0 && wanted > available ? available / wanted : 1;
      var drawnWidth = wanted * factor;
      var drawnHeight = size.height * ppi * factor;

      shapeEl.style.width = drawnWidth + 'px';
      shapeEl.style.height = drawnHeight + 'px';
      shapeEl.style.borderRadius = ROUND[shape] ? '50%' : '0px';

      var label = sizeLabel(shape, size.width, size.height);
      if (shapeText) {
        shapeText.textContent = label;
        shapeText.hidden = Math.min(drawnWidth, drawnHeight) < 74;
      }

      if (customEcho) {
        customEcho.textContent = LOCKED[shape]
          ? mm(size.width) + ' mm'
          : mm(size.width) + ' × ' + mm(size.height) + ' mm';
      }

      if (status) {
        status.textContent = factor < 1
          ? label + ' is wider than this screen, so it is shown at ' + Math.round(factor * 100) + '% of real size. Print the PDF for an exact check.'
          : label + ' shown at real size on your screen.';
      }
      root.classList.toggle('is-scaled-down', factor < 1);

      publishSelection(root, shape, size);
      renderTarget();
      renderReferences();
    }

    function setPpi(next, persist) {
      ppi = clamp(next, MIN_PPI, MAX_PPI);
      slider.value = String(Math.round(ppi));
      if (persist) writeStoredPpi(ppi);
      render();
    }

    slider.min = String(MIN_PPI);
    slider.max = String(MAX_PPI);
    slider.step = '1';
    slider.addEventListener('input', function () {
      setPpi(parseFloat(slider.value), true);
    });

    refButtons.forEach(function (button) {
      if (button.getAttribute('data-truesize-reference') === reference) {
        button.setAttribute('aria-pressed', 'true');
      }
      button.addEventListener('click', function () {
        refButtons.forEach(function (other) {
          other.setAttribute('aria-pressed', String(other === button));
        });
        reference = button.getAttribute('data-truesize-reference');
        render();
      });
    });

    shapeButtons.forEach(function (button) {
      if (button.getAttribute('data-truesize-shape-option') === shape) {
        button.setAttribute('aria-pressed', 'true');
      }
      button.addEventListener('click', function () {
        shapeButtons.forEach(function (other) {
          other.setAttribute('aria-pressed', String(other === button));
        });
        shape = button.getAttribute('data-truesize-shape-option');
        applyShape();
        render();
      });
    });

    [widthInput, heightInput].forEach(function (input) {
      if (!input) return;
      input.addEventListener('input', function () {
        isCustom = true;
        readCustomInputs();
        buildSizeRow();
        render();
      });
      input.addEventListener('blur', function () {
        readCustomInputs();
        syncCustomInputs();
        render();
      });
    });

    if (refToggle) {
      refToggle.addEventListener('click', function () {
        var on = refToggle.getAttribute('aria-pressed') === 'true';
        refToggle.setAttribute('aria-pressed', String(!on));
        renderReferences();
      });
    }

    if (reset) {
      reset.addEventListener('click', function () {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          /* Storage is optional. */
        }
        setPpi(defaultPpi, false);
      });
    }

    if (custom) custom.hidden = false;
    if (typeof window.ResizeObserver === 'function') new window.ResizeObserver(render).observe(stage);
    window.addEventListener('resize', render);

    applyShape();
    root.hidden = false;
    setPpi(ppi, false);
  }

  ready(function () {
    [].slice.call(document.querySelectorAll('[data-spark-truesize]')).forEach(init);
  });

  if (typeof window !== 'undefined') {
    window.SparkSizeGuideTrueSize = {
      publishSelection: publishSelection,
    };
  }
})();
